import {
  CrystalDTO,
  IScribblesDataDTO,
  IWellImageDTO,
  LightPathDTO,
  ROIDTO,
  RegionAnnotationDTO,
  RegionDropLocationDTO,
  RegionPointDTO,
  RegionRulerDTO,
  WellImageDTO,
} from 'business/models/Models';
import {
  AnnotationDrawing,
  CrystalDrawing,
  DropLocationDrawing,
  LightPathDrawing,
  POIDrawing,
  ROIDrawing,
  RulerDrawing,
  ScribbleDrawing,
} from 'components/well/drawingManager/constants';
import { getPlainArray, getPlainObject } from './utils';
import TransformPoints from 'components/plateviewer/well/TransformPoints';
import { scaledRegionScibblePoints } from 'components/well/drawingManager/utils';

export const getPlainTransformedScribbleDrawingData = (
  modelObject: IScribblesDataDTO,
  tpObject: TransformPoints,
): ScribbleDrawing => {
  const [x, y] = tpObject.FromDbToCanvas(
    modelObject.scribbleStartPointsXYofWell.xOffSet,
    modelObject.scribbleStartPointsXYofWell.yOffSet,
  );

  const xoffsetfromcentertostart = tpObject.scalingWidth / 2 + tpObject.leftMargin;
  const yoffsetfromcentertostart = tpObject.scalingHeight / 2 + tpObject.topMargin;

  const points = scaledRegionScibblePoints(
    modelObject.regionScribblePointsArray,
    xoffsetfromcentertostart,
    yoffsetfromcentertostart,
    tpObject,
  );

  return {
    id: modelObject.scribbleStartPointsXYofWell.id,
    points: points,
    x,
    y,
    color: modelObject.scribbleStartPointsXYofWell.color,
    dbX: modelObject.scribbleStartPointsXYofWell.xOffSet,
    dbY: modelObject.scribbleStartPointsXYofWell.yOffSet,
    zIndex: 0,
  };
};

export const getPlainTransformedLightPathDrawingData = (
  modelObject: LightPathDTO,
  tpObject: TransformPoints,
): LightPathDrawing => {
  const transformedLightPath = tpObject.ConvertLightPathToCanvas(modelObject);
  const { id, rectangleF, minFOVSize, maxFOVSize, validSizeList, ...rest } =
    transformedLightPath;
  return {
    ...rest,
    id: parseInt(id),
    rectangleF: getPlainObject(rectangleF),
    minFOVSize: getPlainObject(minFOVSize),
    maxFOVSize: getPlainObject(maxFOVSize),
    validSizeList: validSizeList?.length ? getPlainArray(validSizeList) : [],
    zIndex: 0,
  };
};

export const getPlainTransformedRuler = (
  modelObject: RegionRulerDTO,
  tpObject: TransformPoints,
): RulerDrawing => {
  const { x1: dbX1, y1: dbY1, x2: dbX2, y2: dbY2 } = modelObject;
  const [x1, y1] = tpObject.FromDbToCanvas(dbX1, dbY1);
  const [x2, y2] = tpObject.FromDbToCanvas(dbX2, dbY2);
  return {
    ...getPlainObject(modelObject),
    x1,
    y1,
    x2,
    y2,
    dbX1,
    dbY1,
    dbX2,
    dbY2,
    zIndex: 0,
  };
};

export const getPlainTransformedRoi = (
  modelObject: ROIDTO,
  tpObject: TransformPoints,
): ROIDrawing => {
  const { xCenter, yCenter, width, height } = tpObject.ConvertROIToCanvas(modelObject);
  return {
    id: modelObject.id,
    imagingEnabled: modelObject.imagingEnabled,
    locked: !!modelObject.locked,
    dropNumber: modelObject.dropNumber,
    height,
    regionId: modelObject.regionId,
    regionName: modelObject.regionName,
    width,
    x: xCenter,
    y: yCenter,
    dbWidth: modelObject.xWidth,
    dbHeight: modelObject.yHeight,
    dbXCenterOffset: modelObject.xCenterOffSet,
    dbYCenterOffset: modelObject.yCenterOffSet,
    zIndex: 0,
  } as ROIDrawing;
};

export const getPlainTransformedDropLocation = (
  modelObject: RegionDropLocationDTO,
  tpObject: TransformPoints,
): DropLocationDrawing => {
  const { xCenter, yCenter, width, height } = tpObject.ConvertRectanglePointsToCanvas(
    modelObject.xcenterOffset,
    modelObject.ycenterOffset,
    modelObject.xwidth,
    modelObject.yheight,
  );
  return {
    locked: true,
    height,
    width,
    x: xCenter,
    y: yCenter,
    dbWidth: modelObject.xwidth,
    dbHeight: modelObject.yheight,
    dbXCenterOffset: modelObject.xcenterOffset,
    dbYCenterOffset: modelObject.ycenterOffset,
    zIndex: 0,
  } as DropLocationDrawing;
};

export const getPlainTransformedPoi = (
  modelObject: RegionPointDTO,
  tpObject: TransformPoints,
): POIDrawing => {
  const { x: dbX, y: dbY } = modelObject;
  const [x, y] = tpObject.FromDbToCanvas(dbX, dbY);
  return { ...getPlainObject(modelObject), x, y, dbX, dbY, zIndex: 0 };
};

export const getPlainTransformedCrystal = (
  modelObject: CrystalDTO,
  tpObject: TransformPoints,
): CrystalDrawing => {
  const { x: dbX, y: dbY, width: dbWidth, height: dbHeight } = modelObject;
  const { xCenter, yCenter, width, height } = tpObject?.ConvertRectanglePointsToCanvas(
    dbX,
    dbY,
    dbWidth,
    dbHeight,
  );
  return {
    ...getPlainObject(modelObject),
    x: xCenter,
    y: yCenter,
    width,
    height,
    locked: false,
    isChecked: false,
    dbX,
    dbY,
    dbWidth,
    dbHeight,
    zIndex: 0,
  };
};

export const getPlainTransformedAnnotation = (
  modelObject: RegionAnnotationDTO,
  tpObject: TransformPoints,
): AnnotationDrawing => {
  const { x: dbX, y: dbY } = modelObject;
  const [transformedX, transformedY] = tpObject.FromDbToCanvas(dbX, dbY);
  return {
    ...getPlainObject(modelObject),
    x: transformedX,
    y: transformedY,
    dbX,
    dbY,
    isCollapsed: false,
    zIndex: 0,
  };
};

export const transformWellDropImagesMetaData = (
  imagesMeta: WellImageDTO[] | null | undefined,
): Record<string, IWellImageDTO> => {
  if (!imagesMeta || !Array.isArray(imagesMeta)) {
    return {};
  }

  const result: Record<string, WellImageDTO> = {};

  for (const meta of imagesMeta) {
    result[meta.key] = meta.toJSON();
  }

  return result;
};
